/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  TextField,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  ArrowCircleLeft as ArrowCircleLeftIcon,
  ArrowCircleRight as ArrowCircleRightIcon,
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import Loading from '../../components/Loading';
import { Error } from '../../components/Error';
import { ViewTitle } from '../../components/ViewTitle';

const ReportGold = ({ title, subtitle }) => {
  const dispatch = useDispatch();
  const goldReport = useSelector((state) => state.goldReport);
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [materialFilter, setMaterialFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('day');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [page, setPage] = useState(0); // ใช้ 0-indexed เพื่อความง่ายกับ TablePagination
  const [size, setSize] = useState(100);
  const [tabValue, setTabValue] = useState(0); // 0 = ซื้อ, 1 = ขาย

  const getApiParams = (currentPage, currentSize) => {
    const baseParams = {
      page: currentPage !== undefined ? currentPage + 1 : page + 1,
      size: currentSize || size,
    };

    if (periodFilter === 'all') {
      return { ...baseParams, date: '', month: '', year: '' };
    }

    const selectedDate = dayjs(date);
    switch (periodFilter) {
      case 'day':
        return {
          ...baseParams,
          date: selectedDate.format('YYYY-MM-DD'),
          month: '',
          year: '',
        };
      case 'month':
        return {
          ...baseParams,
          date: '',
          month: selectedDate.format('YYYY-MM'),
          year: '',
        };
      case 'year':
        return {
          ...baseParams,
          date: '',
          month: '',
          year: selectedDate.format('YYYY'),
        };
      default:
        return {
          ...baseParams,
          date: selectedDate.format('YYYY-MM-DD'),
          month: '',
          year: '',
        };
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // หมายเหตุ: ถ้าต้องการ pagination แยกตาม tab จริงๆ ต้องส่ง type ไป API ด้วย
        const apiParams = getApiParams(page, size);
        await dispatch(actions.goldReportAll(apiParams));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch, date, periodFilter, page, size]);

  const allReports = goldReport?.rows || [];

  const materialTypes = useMemo(
    () =>
      _.uniq(
        allReports.flatMap((item) =>
          item?.order
            ?.map((orderItem) => orderItem?.material?.material_type?.name)
            .filter(Boolean),
        ),
      ),
    [allReports],
  );

  // 1. กรองตาม Material ก่อน
  const filteredByMaterial = useMemo(() => {
    if (materialFilter === 'all') return allReports;
    return allReports.filter((item) =>
      item?.order?.some(
        (orderItem) =>
          orderItem?.material?.material_type?.name === materialFilter,
      ),
    );
  }, [allReports, materialFilter]);

  // 2. แยกตาม Type
  const buyData = useMemo(
    () =>
      filteredByMaterial.filter(
        (item) => item?.gold_transaction_type === 'BUY',
      ),
    [filteredByMaterial],
  );
  const sellData = useMemo(
    () =>
      filteredByMaterial.filter(
        (item) => item?.gold_transaction_type !== 'BUY',
      ),
    [filteredByMaterial],
  );

  const currentTableData = tabValue === 0 ? buyData : sellData;

  // คำนวณ Totals (ใช้ filteredByMaterial เพื่อให้แสดงผลรวมทั้งหมดได้ถูกต้อง)
  const totals = useMemo(() => {
    return filteredByMaterial.reduce(
      (acc, item) => {
        const itemCost = _.sumBy(item?.order || [], 'cost');
        const itemWeight = _.sumBy(item?.order || [], 'weight');
        const isBuy = item?.gold_transaction_type === 'BUY';
        const itemVat = _.sumBy(item?.order || [], 'vat');

        if (isBuy) {
          acc.buyCount += 1;
          acc.buyCost += itemCost;
          acc.buyWeight += itemWeight;
          acc.buyVat += itemVat;
        } else {
          acc.saleCount += 1;
          acc.saleCost += itemCost;
          acc.saleWeight += itemWeight;
          acc.saleVat += itemVat;
        }
        return acc;
      },
      {
        buyCount: 0,
        saleCount: 0,
        buyCost: 0,
        saleCost: 0,
        buyWeight: 0,
        saleWeight: 0,
        buyVat: 0,
        saleVat: 0,
      },
    );
  }, [filteredByMaterial]);

  const handleDetailTransaction = (id) => history.push(`report/detail/${id}`);

  const handleDateChange = (days) => {
    if (periodFilter !== 'all') {
      setDate(dayjs(date).add(days, periodFilter).format('YYYY-MM-DD'));
    }
    setPage(0);
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriodFilter(newPeriod);
    if (newPeriod !== 'all') {
      setDate(dayjs().format('YYYY-MM-DD'));
    }
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleDeleteSingle = (id) => setDeleteDialog({ open: true, id });

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await dispatch(actions.goldReportDelete(deleteDialog.id));
      const apiParams = getApiParams(0, size); // Reload page 0
      await dispatch(actions.goldReportAll(apiParams));
      setPage(0);
      setDeleteDialog({ open: false, id: null });
      alert('ลบรายงานเรียบร้อยแล้ว');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPeriodDisplayText = () => {
    if (periodFilter === 'all') return 'ข้อมูลทั้งหมด';
    const selectedDate = dayjs(date);
    const formats = {
      day: 'DD MMMM YYYY',
      month: 'MMMM YYYY',
      year: 'YYYY',
    };
    return selectedDate.format(formats[periodFilter]);
  };

  const getNavigationText = () => {
    const navTexts = {
      day: { prev: 'วันก่อนหน้า', next: 'วันถัดไป' },
      month: { prev: 'เดือนก่อนหน้า', next: 'เดือนถัดไป' },
      year: { prev: 'ปีก่อนหน้า', next: 'ปีถัดไป' },
    };
    return navTexts[periodFilter] || { prev: 'ก่อนหน้า', next: 'ถัดไป' };
  };

  function formatNumber(value) {
    if (value == null || value === '') return '';
    return Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (goldReport.isLoading || loading) return <Loading />;

  if (goldReport.isCompleted) {
    const navText = getNavigationText();

    return (
      <Box sx={{ p: 2 }}>
        <ViewTitle title={title} subtitle={subtitle} />

        <Card sx={{ p: 3, mb: 3, mt: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            เลือกช่วงเวลาและกรองข้อมูล
          </Typography>
          {periodFilter !== 'all' && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                p: 2,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowCircleLeftIcon />}
                onClick={() => handleDateChange(-1)}
                sx={{ fontSize: '1rem' }}
              >
                {navText.prev}
              </Button>
              <Typography
                variant="h5"
                sx={{ fontWeight: 'bold', color: '#1976d2' }}
              >
                {getPeriodDisplayText()}
              </Typography>
              <Button
                variant="outlined"
                endIcon={<ArrowCircleRightIcon />}
                onClick={() => handleDateChange(1)}
                sx={{ fontSize: '1rem' }}
              >
                {navText.next}
              </Button>
            </Box>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>ช่วงเวลา</InputLabel>
                <Select
                  value={periodFilter}
                  label="ช่วงเวลา"
                  onChange={(e) => handlePeriodChange(e.target.value)}
                >
                  <MenuItem value="day">รายวัน</MenuItem>
                  <MenuItem value="month">รายเดือน</MenuItem>
                  <MenuItem value="year">รายปี</MenuItem>
                  <MenuItem value="all">ทั้งหมด</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {periodFilter !== 'all' && (
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    renderInput={(props) => <TextField {...props} fullWidth />}
                    label="เลือกวันที่"
                    inputFormat="DD/MM/YYYY"
                    value={dayjs(date)}
                    onChange={(newValue) => {
                      setDate(newValue.format('YYYY-MM-DD'));
                      setPage(0);
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>ประเภทสินค้า</InputLabel>
                <Select
                  value={materialFilter}
                  label="ประเภทสินค้า"
                  onChange={(e) => setMaterialFilter(e.target.value)}
                >
                  <MenuItem value="all">ทั้งหมด</MenuItem>
                  {materialTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Card>

        {/* Summary - แยกซ้ายขวา */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, bgcolor: '#ffebee', height: '100%' }}>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: 'bold', color: '#d32f2f' }}
              >
                สรุปรายงานซื้อ (รับเข้า)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="text.secondary">จำนวนรายการ:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {totals.buyCount} รายการ
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">น้ำหนักรวม:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {totals.buyWeight.toFixed(3)} กรัม
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">มูลค่ารวม:</Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: '#d32f2f' }}
                  >
                    {formatNumber(totals.buyCost)} บาท
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">ภาษีรวม:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(totals.buyVat)} บาท
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, bgcolor: '#e8f5e9', height: '100%' }}>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: 'bold', color: '#2e7d32' }}
              >
                สรุปรายงานขาย (ออก)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="text.secondary">จำนวนรายการ:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {totals.saleCount} รายการ
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">น้ำหนักรวม:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {totals.saleWeight.toFixed(3)} กรัม
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">มูลค่ารวม:</Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: '#2e7d32' }}
                  >
                    {formatNumber(totals.saleCost)} บาท
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary">ภาษีรวม:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatNumber(totals.saleVat)} บาท
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs and Table */}
        <Card sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              textColor={tabValue === 0 ? 'error' : 'success'}
              indicatorColor={tabValue === 0 ? 'error' : 'success'}
            >
              <Tab
                label={`รายงานซื้อ (${totals.buyCount})`}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  bgcolor: tabValue === 0 ? '#ffebee' : 'transparent',
                  transition: 'background-color 0.3s',
                }}
              />
              <Tab
                label={`รายงานขาย (${totals.saleCount})`}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  bgcolor: tabValue === 1 ? '#e8f5e9' : 'transparent',
                  transition: 'background-color 0.3s',
                }}
              />
            </Tabs>
          </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{ bgcolor: tabValue === 0 ? '#ffebee' : '#e8f5e9' }}
                >
                  {[
                    'ลำดับ',
                    'ประเภท',
                    'รายการ',
                    'มูลค่า',
                    'ภาษี',
                    'น้ำหนัก',
                    'วันที่',
                    'การดำเนินการ',
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{ fontWeight: 'bold', fontSize: '1.1rem', py: 2 }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentTableData.length > 0 ? (
                  currentTableData
                    // ถ้า API ยังไม่ได้แยก type อาจจะต้อง slice เองที่นี่ถ้าต้องการ pagination แยก
                    // .slice(page * size, page * size + size)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((row, index) => {
                      const isBuy = row?.gold_transaction_type === 'BUY';

                      return (
                        <TableRow
                          key={row._id}
                          sx={{
                            bgcolor: isBuy
                              ? index % 2 === 0
                                ? '#fff'
                                : '#fafafa'
                              : index % 2 === 0
                              ? '#fff'
                              : '#f9fbe7', // สีสลับสำหรับแถวขาย
                            '&:hover': { bgcolor: '#f5f5f5' },
                          }}
                        >
                          <TableCell
                            sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                          >
                            {index + 1 + page * size}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isBuy ? 'ซื้อ' : 'ขาย'}
                              color={isBuy ? 'error' : 'success'}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell>
                            {row?.order?.length > 0 ? (
                              <Box>
                                {row.order.map((item, idx) => (
                                  <Box key={idx}>
                                    <Chip
                                      label={`${item?.material?.name || '-'}${
                                        item?.place === 'คลังรูปพรรณใหม่'
                                          ? ' ใหม่'
                                          : item?.place
                                          ? ` ${item.place}`
                                          : ''
                                      }(${
                                        item?.material?.material_type?.name ||
                                        '-'
                                      })`}
                                      variant="outlined"
                                      size="small"
                                      sx={{ display: 'inline-flex', mb: 0.5 }}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Chip
                                label="ไม่มีสินค้า"
                                color="default"
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 'bold',
                              color: isBuy ? '#d32f2f' : '#2e7d32',
                            }}
                          >
                            {_.map(row?.order, (item, i) => (
                              <div key={i}>{formatNumber(item.cost)}</div>
                            ))}
                          </TableCell>
                          <TableCell>
                            {_.map(row?.order, (item, i) => (
                              <div key={i}>{formatNumber(item.vat)}</div>
                            ))}
                          </TableCell>
                          <TableCell>
                            {_.map(row?.order, (item, i) => (
                              <div key={i}>{item.weight.toFixed(3)}</div>
                            ))}
                          </TableCell>
                          <TableCell>
                            {dayjs(row?.date).format('DD/MM/YYYY')}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="outlined"
                                startIcon={<DescriptionIcon />}
                                onClick={() => handleDetailTransaction(row._id)}
                                size="small"
                                color={isBuy ? 'error' : 'success'}
                              >
                                รายละเอียด
                              </Button>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteSingle(row._id)}
                                size="small"
                                sx={{
                                  bgcolor: '#ffebee',
                                  '&:hover': { bgcolor: '#ffcdd2' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="h6" color="text.secondary">
                        ไม่มีรายงาน{tabValue === 0 ? 'ซื้อ' : 'ขาย'}
                        ในช่วงเวลานี้
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <TablePagination
            component="div"
            // ใช้ length ของ currentTableData ถ้า pagination เป็น client-side
            // count={goldReport?.total || 0}
            count={currentTableData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={size}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="แสดงต่อหน้า:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`
            }
          />
        </Box>

        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, id: null })}
        >
          <DialogTitle>ยืนยันการลบรายงาน</DialogTitle>
          <DialogContent>
            <DialogContentText>
              คุณต้องการลบรายงานนี้หรือไม่?
              <br />
              <strong>การดำเนินการนี้ไม่สามารถยกเลิกได้</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
              ยกเลิก
            </Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              ยืนยันการลบ
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return <Error message={goldReport?.message} />;
};

ReportGold.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

ReportGold.defaultProps = {
  title: '',
  subtitle: '',
};

export default ReportGold;
